import { formattedCurrency, withStyles } from 'vopay';
import { useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import LottieFile from '../../components/LottieFile';
import LottieBalance from '../../LottieFiles/Balances.json';
import ExpandCard from '../../components/ExpandCard';
import type { LottieRefCurrentProps } from 'lottie-react';
import styles from './InteractiveInfo.module.scss';
import { Title } from 'UI';
import { AccountBalance } from '@/hooks/useApi/useFetchAccountBalance';
import Skeleton from '../../widgets/Skeleton';

const cn = withStyles(styles);

type TotalBalanceCardProps = {
  accountBalance: AccountBalance | null;
  loading: boolean;
};

export default function TotalBalanceCard({ accountBalance, loading }: TotalBalanceCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const balanceRef = useRef<LottieRefCurrentProps>(null);
  const { permissions } = usePermissions();

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  return (
    <ExpandCard
      align="start"
      buttonText={t('Widgets.InteractiveInfo.openAccountSummary')}
      showButton={permissions.ViewAccountSummary}
      onMouseOver={() => handleLottieFileHover(balanceRef, true)}
      onMouseOut={() => handleLottieFileHover(balanceRef, false)}
      disabled={loading}
      onNavigateClick={() => onSubNavClick('accountSummary', '/account-summary', t('balances'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalBalance')}
            </Title>

            {loading ? (
              <Title level="3" className={cn('totalCardAmount')}>
                <Skeleton />
              </Title>
            ) : accountBalance ? (
              <>
                <div className={cn('totalCardCurrency')}>
                  <Title level="5" emphasized={false}>
                    {accountBalance.Currency}
                  </Title>

                  <Title level="3" className={cn('totalCardAmount')}>
                    {accountBalance && formattedCurrency(accountBalance.AccountBalance)}
                  </Title>
                </div>
              </>
            ) : null}
          </div>

          <LottieFile animationData={LottieBalance} lottieRef={balanceRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          {accountBalance ? (
            <>
              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.currentBalance')}</div>
                <div>
                  {accountBalance.Currency} {formattedCurrency(accountBalance.AccountBalance)}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.pendingBalance')}</div>
                <div>
                  {accountBalance.Currency} {formattedCurrency(accountBalance.PendingFunds)}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.reserveBalance')}</div>
                <div>
                  {accountBalance.Currency} {formattedCurrency(accountBalance.Reserve)}
                </div>
              </div>

              <div className={cn('totalBalanceCard')}>
                <div>{t('Widgets.InteractiveInfo.availableFunds')}</div>
                <div>
                  {accountBalance.Currency} {formattedCurrency(accountBalance.AvailableFunds)}
                </div>
              </div>
            </>
          ) : null}
        </div>
      }
    />
  );
}
